"use client";

import type { SocialLevel } from "@/lib/social-ratings/levels";
import { cn } from "cn";
import { useTranslations } from "next-intl";

/**
 * The three self-assessment levels (#1029). Labels only — the numeric seeds
 * behind them are deliberately never shown to the user.
 */
export function LevelSelect({
    value,
    onChange,
    disabled,
}: {
    value: SocialLevel | null;
    onChange: (level: SocialLevel) => void;
    disabled?: boolean;
}) {
    const t = useTranslations("social");
    const options: { level: SocialLevel; label: string; hint: string }[] = [
        {
            level: "beginner",
            label: t("levelBeginner"),
            hint: t("levelBeginnerHint"),
        },
        {
            level: "intermediate",
            label: t("levelIntermediate"),
            hint: t("levelIntermediateHint"),
        },
        {
            level: "advanced",
            label: t("levelAdvanced"),
            hint: t("levelAdvancedHint"),
        },
    ];
    return (
        <div
            className="grid gap-2"
            role="radiogroup"
            aria-label={t("levelTitle")}
        >
            {options.map((o) => (
                <button
                    key={o.level}
                    type="button"
                    role="radio"
                    aria-checked={value === o.level}
                    disabled={disabled}
                    onClick={() => onChange(o.level)}
                    className={cn(
                        "rounded-lg border p-3 text-left transition-colors",
                        value === o.level
                            ? "border-primary bg-primary/5"
                            : "border-neutral-200 hover:border-neutral-400",
                    )}
                >
                    <span className="block font-medium">{o.label}</span>
                    <span className="block text-sm text-neutral-500">
                        {o.hint}
                    </span>
                </button>
            ))}
        </div>
    );
}
